import { CheckIcon } from 'lucide-react'
import { ReactNode } from 'react'

import { cn } from '../../utils/cn'
import { Card, CardContent } from './card'

export type CardCheckboxSize = 'default' | 'sm'

/** `sm` pins an explicit h-10 so a row of cards lines up with default-size Inputs and Buttons. */
const SIZES: Record<CardCheckboxSize, { card: string; content: string; label: string; box: string }> = {
  default: {
    card: 'px-1 py-3',
    content: 'px-4',
    label: 'text-md md:text-sm',
    box: 'size-5',
  },
  sm: {
    card: 'h-10 justify-center px-1 py-0',
    content: 'px-3',
    label: 'text-sm',
    box: 'size-4',
  },
}

const THEMES = {
  light: {
    card: 'border-input',
    checked: 'border-ink-900',
    hover: 'hover:border-ink-300',
    label: 'text-black',
    // Darker than the card's own border: ink-200 on a light ground read as an empty gap.
    box: 'border-ink-400',
  },
  dark: {
    card: 'bg-ink-800 border-ink-700',
    checked: 'border-ink-200',
    hover: 'hover:border-ink-500',
    label: 'text-ink-50',
    box: 'border-ink-500',
  },
} as const

interface CardCheckboxProps {
  isChecked: boolean
  onChange: (checked: boolean) => void
  children: ReactNode
  className?: string
  contentClassName?: string
  disabled?: boolean
  /** Shown as it is, not dimmed, and can't be changed; for a choice that is already made. */
  readOnly?: boolean
  /** `sm` matches the default Input height — single-line labels only. */
  size?: CardCheckboxSize
  /** Inverts the card for an ink-800/900 ground. */
  onDark?: boolean
}

export function CardCheckbox({
  isChecked,
  onChange,
  children,
  className,
  contentClassName,
  disabled,
  readOnly,
  size = 'default',
  onDark = false,
}: CardCheckboxProps) {
  const sizing = SIZES[size]
  const theme = THEMES[onDark ? 'dark' : 'light']
  const locked = disabled || readOnly

  function toggle() {
    if (!locked) onChange(!isChecked)
  }

  return (
    <Card
      asChild
      className={cn(
        'focus-visible:ring-ring/50 shadow-none transition-colors outline-none focus-visible:ring-2',
        theme.card,
        sizing.card,
        disabled ? 'opacity-50' : !readOnly && 'cursor-pointer',
        isChecked ? theme.checked : !locked && theme.hover,
        className
      )}
    >
      {/* A real button keeps keyboard activation native; the loop came from the nested Radix control. */}
      <button
        type="button"
        role="checkbox"
        aria-checked={isChecked}
        aria-readonly={readOnly || undefined}
        data-state={isChecked ? 'checked' : 'unchecked'}
        disabled={disabled}
        onClick={toggle}
        className="w-full text-left"
      >
        <CardContent className={sizing.content}>
          <div className={cn('flex items-center justify-between gap-4', contentClassName)}>
            <div className={cn('flex-1 font-semibold', theme.label, sizing.label)}>{children}</div>
            {/* Presentational: a real Radix Checkbox here re-dispatches a bubbling click and loops. */}
            <span
              aria-hidden="true"
              className={cn(
                'grid shrink-0 place-content-center rounded-[4px] border transition-colors',
                sizing.box,
                isChecked ? 'bg-primary border-primary text-primary-foreground' : theme.box
              )}
            >
              {isChecked && <CheckIcon className="size-3.5" />}
            </span>
          </div>
        </CardContent>
      </button>
    </Card>
  )
}
